"use client";

import CaseDataLoader from "./CaseDataLoader";
import styled from "styled-components";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import CheckIcon from "@/assets/icons/Check.svg";
import Button from "../Button/Button";
import Image from "next/image";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { DismissUploadToast } from "../ToastUtils/UploadToast";
import { UploadMutationType } from "@/lib/hooks/useCaseUpload";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

const CaseToast = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  font-size: 1.5rem;
  gap: 10px;
  background-color: ${(props) => props.theme.white};
  border-radius: 10px;
  min-height: 66px;

  .complete-text {
    color: ${(props) => props.theme.green};
    min-width: 200px;
  }

  .toast-p span {
    color: ${(props) => props.theme.main};
  }
`;

interface IToast {
  percent: number | null;
  uploadMutation: UploadMutationType;
  toastId: string;
}

export default function CaseDataToast({
  percent,
  uploadMutation,
  toastId,
}: IToast) {
  const setModalType = useBoundStore((state) => state.setModalType);
  const caseUploadStatus = useBoundStore((state) => state.caseUploadStatus);
  const setCaseUploadStatus = useBoundStore(
    (state) => state.setCaseUploadStatus
  );
  const setUploadProgress = useBoundStore((state) => state.setUploadProgress);
  const tCaseData = useTranslations("toast.caseDataToast");
  const tComponents = useTranslations("componentText");

  const [isProcessing, setIsProcessing] = useState(false);

  const handleCloseToast = () => {
    DismissUploadToast();
    if (caseUploadStatus) {
      setCaseUploadStatus(null);
      setUploadProgress(0);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (percent !== null && percent >= 100) {
      setIsProcessing(true);

      const timer = setTimeout(() => {
        handleCloseToast();
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [percent]);

  return (
    <CaseToast key={toastId}>
      {caseUploadStatus?.status === "COMPLETED" ? (
        <>
          <Image alt="check-toast" src={CheckIcon.src} height={25} width={28} />
          <p className="toast-p johnson-display complete-text">
            {tCaseData("uploaded")}
          </p>
          <Image
            alt="close-toast"
            src={CloseIcon.src}
            height={25}
            width={25}
            style={{ filter: "brightness(0)", cursor: "pointer" }}
            onClick={handleCloseToast}
          />
        </>
      ) : caseUploadStatus?.status === "FAILED" ? (
        <>
          <Image
            alt="close-toast"
            src={CloseIcon.src}
            height={30}
            width={30}
            style={{ cursor: "pointer" }}
            onClick={handleCloseToast}
          />
          <p className="toast-p johnson-display">
            {tCaseData.rich("error", {
              link: (chunks) => <span>{chunks}</span>,
            })}
          </p>
          <Button
            label={tComponents("retry")}
            onPress={() => {
              handleCloseToast(); // dismiss toast
              setModalType("upload-case-data");
            }}
            theme="primary"
            name="retry-upload"
          />
        </>
      ) : uploadMutation.isError ? (
        <>
          <Image
            alt="close-toast"
            src={CloseIcon.src}
            height={30}
            width={30}
            style={{ cursor: "pointer" }}
            onClick={handleCloseToast}
          />
          <p className="toast-p johnson-display">
            {tCaseData.rich("failed", {
              link: (chunks) => <span>{chunks}</span>,
            })}
          </p>
          <Button
            label={tComponents("retry")}
            onPress={() => {
              handleCloseToast(); // dismiss toast
              setModalType("upload-case-data");
            }}
            theme="primary"
            name="retry-upload"
          />
        </>
      ) : isProcessing ? (
        <>
          <Image alt="check-toast" src={CheckIcon.src} height={25} width={28} />
          <p className="toast-p johnson-display">{tCaseData("complete")}</p>
          <Image
            alt="close-toast"
            src={CloseIcon.src}
            height={25}
            width={25}
            style={{ filter: "brightness(0)", cursor: "pointer" }}
            onClick={handleCloseToast}
          />
        </>
      ) : (
        caseUploadStatus?.status !== "PROCESSING" && (
          <>
            <CaseDataLoader percent={percent} size="46px" />
            <p className="toast-p johnson-display"> {tCaseData("uploading")}</p>
          </>
        )
      )}
    </CaseToast>
  );
}

{
  /* <>
  <CaseDataLoader
    percent={caseUploadStatus?.status === "PROCESSING" ? null : percent}
    size="46px"
  />
  <p className="toast-p johnson-display">
    {percent !== null && caseUploadStatus?.status !== "PROCESSING"
      ? "Uploading Case Data"
      : "Processing..."}
  </p>
</>; */
}
